import {
  applyCampaignOverlayToDocRow,
  campaignDefinedVariables,
  claimDocVariableOntoCampaign,
  findCampaignOverlay,
} from './campaignOverlay';
import { DOC_VARIABLE_SOURCE_NAME, MISSION_VARIABLE_SOURCE_NAME, nameKey, normalizeVariableName } from './names';
import {
  VARIABLE_TYPE_ARRAY,
  isArrayVariable,
  normalizeArrayValues,
} from './types';
import { countFilledVariableSlots } from './values';

/** Walk campaign → ancestors (current first). Guards parentSprintId cycles. */
const walkCampaignChain = (campaign, allSprints = []) => {
  const chain = [];
  const visited = new Set();
  let current = campaign;
  while (current && current.id && !visited.has(current.id)) {
    visited.add(current.id);
    chain.push(current);
    const parentId = current.parentSprintId;
    current = parentId ? allSprints.find((s) => s.id === parentId) : null;
  }
  return chain;
};

/**
 * Flatten every variable visible from a doc's position into ONE ordered
 * list, highest scope first (root campaign → … → campaign → doc). Later
 * duplicates of a name are marked `shadowed: true` (they lose to the
 * higher definition).
 *
 * @returns {Array} [{ …row, name, scope: 'doc'|'campaign', sourceId, sourceName, shadowed }]
 */
export const collectScopedVariables = ({
  docVariables = [],
  campaign = null,
  allSprints = [],
  missionVariables = null,
}) => {
  const out = [];
  const seen = new Set();

  const push = (rows, scope, sourceId, sourceName) => {
    for (const row of rows || []) {
      const name = normalizeVariableName(row.name);
      if (!name) continue;
      const key = nameKey(name);
      out.push({ ...row, name, scope, sourceId, sourceName, shadowed: seen.has(key) });
      seen.add(key);
    }
  };

  const chain = walkCampaignChain(campaign, allSprints);
  for (let i = chain.length - 1; i >= 0; i--) {
    push(
      campaignDefinedVariables(chain[i].variables),
      'campaign',
      chain[i].id,
      chain[i].name || 'Campaign'
    );
  }

  push(docVariables, 'doc', null, DOC_VARIABLE_SOURCE_NAME);

  return out.map((row) => {
    if (row.scope !== 'doc' || row.shadowed) return row;
    const view = presentDocVariable(row, { campaign, allSprints, missionVariables });
    return { ...view, scope: 'doc', sourceId: row.sourceId, shadowed: false };
  });
};

/**
 * Where a doc variable's value lives.
 * `valueScope: 'mission'` is explicit. A mission value copy with no flag is
 * the same choice (it was saved on the mission). Everything else is the document.
 */
export const docRowScopeChoice = (row, missionVariables) => {
  if (!row) return 'doc';
  if (row.valueScope === 'mission') return 'mission';
  if (row.valueScope === 'doc') return 'doc';
  if (Array.isArray(missionVariables) && findCampaignOverlay(missionVariables, row)) {
    return 'mission';
  }
  return 'doc';
};

/**
 * Value a doc row shows in this context.
 * Document scope: the document's value, or this campaign's existing value
 * copy until the value is saved onto the document (`valueScope: 'doc'`).
 * Mission scope: this mission's copy, otherwise the document value.
 */
export const presentDocVariable = (
  docRow,
  { campaign = null, allSprints = [], missionVariables = null } = {}
) => {
  if (!docRow) return docRow;
  const choice = docRowScopeChoice(docRow, missionVariables);
  if (choice === 'mission') {
    const mission = Array.isArray(missionVariables)
      ? findCampaignOverlay(missionVariables, docRow)
      : null;
    const next = mission ? applyCampaignOverlayToDocRow(docRow, mission) : docRow;
    return { ...next, sourceName: MISSION_VARIABLE_SOURCE_NAME };
  }
  if (docRow.valueScope === 'doc') {
    return { ...docRow, sourceName: DOC_VARIABLE_SOURCE_NAME };
  }
  const overlay = campaign ? findNearestDocOverlay(campaign, allSprints, docRow) : null;
  const next = overlay ? applyCampaignOverlayToDocRow(docRow, overlay) : docRow;
  return { ...next, sourceName: DOC_VARIABLE_SOURCE_NAME };
};

/**
 * Nearest campaign value copy for a doc variable (current campaign, then
 * ancestors). Matches a flagged overlay, or a legacy copy that still uses
 * the doc variable's id. Real campaign variables are not overlays.
 */
export const findNearestDocOverlay = (campaign, allSprints, docVariable) => {
  if (!docVariable) return null;
  for (const sprint of walkCampaignChain(campaign, allSprints)) {
    const hit = findCampaignOverlay(sprint.variables, docVariable);
    if (!hit) continue;
    if (hit.docOverlay || hit.id === docVariable.id) return hit;
  }
  return null;
};

/** @see presentDocVariable */
export const displayedDocVariable = (docRow, ctx = {}) => presentDocVariable(docRow, ctx);

export const countMissionDisplayedFilledSlots = (docVariables = [], ctx = {}) => {
  let n = 0;
  for (const doc of docVariables || []) {
    if (!doc || doc.locked) continue;
    n += countFilledVariableSlots(displayedDocVariable(doc, ctx));
  }
  return n;
};

/** Claim empty overlays onto the mission so this mission goes blank. */
export const emptyMissionViewVariableValues = (
  missionVariables = [],
  docVariables = [],
  ctx = {}
) => {
  let next = Array.isArray(missionVariables) ? missionVariables : [];
  for (const doc of docVariables || []) {
    if (!doc || doc.locked) continue;
    if (docRowScopeChoice(doc, ctx.missionVariables) !== 'mission') continue;
    const shown = displayedDocVariable(doc, ctx);
    if (countFilledVariableSlots(shown) === 0) continue;
    next = claimDocVariableOntoCampaign(next, doc, {
      value: '',
      ...(isArrayVariable(shown)
        ? {
            type: VARIABLE_TYPE_ARRAY,
            values: normalizeArrayValues(shown.values).map(() => ''),
          }
        : {}),
    });
  }
  return next;
};

/** Resolve a name against a scoped list — the highest (first) match wins. */
export const resolveVariableFrom = (scoped, name) => {
  const key = nameKey(name);
  if (!key) return null;
  return scoped.find((v) => nameKey(v.name) === key) || null;
};

/**
 * Names owned by higher levels — used by the variables panel to warn when a
 * lower-scope row duplicates a name it can never win against.
 *
 * @returns {{ forDoc: Map, forCampaign: Map }} lowercase name → sourceName
 */
export const collectHigherScopeNames = ({ campaign = null, allSprints = [] }) => {
  const forDoc = new Map();
  const forCampaign = new Map();
  const chain = walkCampaignChain(campaign, allSprints);

  for (let i = chain.length - 1; i >= 0; i--) {
    const sourceName = chain[i].name || 'Campaign';
    for (const row of campaignDefinedVariables(chain[i].variables)) {
      const key = nameKey(row.name);
      if (!key) continue;
      if (!forDoc.has(key)) forDoc.set(key, sourceName);
      if (i > 0 && !forCampaign.has(key)) forCampaign.set(key, sourceName);
    }
  }

  return { forDoc, forCampaign };
};

/**
 * Ancestor campaigns of `campaign` (nearest first), each with its variable
 * rows — used by the variables panel to show read-only inherited groups.
 */
export const collectAncestorVariableGroups = (campaign, allSprints = []) => {
  const groups = [];
  const visited = new Set(campaign?.id ? [campaign.id] : []);
  let current = campaign?.parentSprintId
    ? allSprints.find((s) => s.id === campaign.parentSprintId)
    : null;
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    groups.push({
      sourceId: current.id,
      sourceName: current.name || 'Campaign',
      variables: campaignDefinedVariables(current.variables).filter((v) =>
        normalizeVariableName(v.name)
      ),
    });
    const parentId = current.parentSprintId;
    current = parentId ? allSprints.find((s) => s.id === parentId) : null;
  }
  return groups;
};

/**
 * Active inherited variables from ancestor campaigns only (highest first).
 * One row per name — shadowed lower-ancestor duplicates are omitted.
 */
export const collectInheritedVariables = (campaign, allSprints = []) => {
  const groups = collectAncestorVariableGroups(campaign, allSprints);
  const out = [];
  const seen = new Set();
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i];
    for (const row of g.variables || []) {
      const name = normalizeVariableName(row.name);
      if (!name) continue;
      const key = nameKey(name);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        ...row,
        name,
        scope: 'inherited',
        sourceId: g.sourceId,
        sourceName: g.sourceName,
      });
    }
  }
  return out;
};

/** True when an inherited table row is a shared-doc default, not an ancestor. */
export const isDocDefaultInherited = (variable) =>
  !!variable && variable.scope === 'inherited' && !variable.sourceId;

/**
 * Doc defaults the current campaign has not claimed — shown as inherited
 * "This doc" rows so value edits can copy-on-write onto the campaign.
 */
export const collectDocDefaultsAsInherited = (docVariables = [], takenNames = []) => {
  const taken = new Set(
    (takenNames || []).map((n) => nameKey(n)).filter(Boolean)
  );
  const out = [];
  for (const row of docVariables || []) {
    const name = normalizeVariableName(row.name);
    if (!name) continue;
    const key = nameKey(name);
    if (taken.has(key)) continue;
    taken.add(key);
    out.push({
      ...row,
      name,
      scope: 'inherited',
      sourceId: null,
      sourceName: DOC_VARIABLE_SOURCE_NAME,
    });
  }
  return out;
};
